import { useSmarticoConfigs } from '@/stores/smartico/configs';
import {
  USER_V2_MENU,
  USER_V2_SIDEBAR_MENU,
  type UserV2MenuEntry,
} from '@/components/user-v2/types';

// Visibilidade de itens do Minha Conta V2 conforme as MESMAS flags do menu
// antigo (components/user/menu/index.vue): IRPF (permissão env), Notificações
// (Smartico ativo && !só-saque). Itens sem `flag` são sempre visíveis.
// ("Indique e Ganhe" existia aqui com a mesma paridade, mas foi removido do
// menu a pedido do time — ficava travado "Em breve" sem uso nenhum.)
export default function useUserV2Menu() {
  const { t } = useTexts();
  const { isIrpfEnabled } = useFeatureFlagsEnv();
  const { userCanOnlyWithdraw } = useUserLimits();
  const { isNotificationActived } = storeToRefs(useSmarticoConfigs());
  const { userCurrency } = useUserData();

  const isNotificationsVisible = computed<boolean>(
    () => !!isNotificationActived.value && !userCanOnlyWithdraw.value,
  );

  function isVisible(entry: UserV2MenuEntry): boolean {
    switch (entry.flag) {
      case 'irpf':
        return isIrpfEnabled.value;

      case 'notifications':
        return isNotificationsVisible.value;

      // agência/conta é formato do sistema bancário brasileiro; MXN usa
      // CLABE (outra tela, ainda sem equivalente aqui) — mesmo gate de
      // components/user-v2/ScreenBankAccounts.vue.
      case 'bank-accounts':
        return userCurrency.value === 'BRL';

      default:
        return true;
    }
  }

  function decorate(entry: UserV2MenuEntry): UserV2MenuEntry {
    // mesma chave/regra do menu antigo (income_report_external.menu_title):
    // ano da declaração = ano corrente - 1, calculado em runtime pra não
    // ficar preso
    const decorated =
      entry.flag === 'irpf'
        ? {
            ...entry,
            label: t('income_report_external.menu_title', {
              year: String(new Date().getFullYear() - 1),
            }),
          }
        : entry;

    // recursivo: filtra/decora os atalhos também (ex.: "Contas Bancárias"
    // dentro de "Carteira"), senão um filho com `flag` sempre apareceria —
    // isVisible/decorate hoje só rodava nos itens de topo.
    if (decorated.children) {
      return {
        ...decorated,
        children: decorated.children.filter(isVisible).map(decorate),
      };
    }

    return decorated;
  }

  const menu = computed<UserV2MenuEntry[]>(() =>
    USER_V2_MENU.filter(isVisible).map(decorate),
  );

  const sidebarMenu = computed<UserV2MenuEntry[]>(() =>
    USER_V2_SIDEBAR_MENU.filter(isVisible).map(decorate),
  );

  return {
    menu,
    sidebarMenu,
    isNotificationsVisible,
    isIrpfEnabled,
  };
}
